// The selected option as IFC4: project → site → building → storeys, and
// every flat, core, corridor, shop, car park and room as an IfcSpace
// extruded from its outline, with its type and usable area as properties.
//
// Spaces only, no walls: this is the feasibility model an architect starts
// the BIM from (what Architechtures calls LOD 200 and TestFit does not
// export at all), not a construction model. Coordinates are local metres;
// the site's placement carries the Stereo 70 offset when there is one.
import type { Generated } from "../generate.ts";
import type { MultiPoly, Pt } from "../geom.ts";

class Step {
  private n = 0;
  readonly lines: string[] = [];
  add(entity: string): string { const id = `#${++this.n}`; this.lines.push(`${id}=${entity};`); return id; }
}

const str = (s: string) => `'${s.replace(/\\/g, "\\\\").replace(/'/g, "''").replace(/[^\x20-\x7e]/g, (c) => `\\X2\\${c.charCodeAt(0).toString(16).toUpperCase().padStart(4, "0")}\\X0\\`)}'`;
const real = (x: number) => (Number.isInteger(x) ? `${x}.` : String(+x.toFixed(4)));

/** A 22-character IFC GlobalId from a counter: unique within the file, which is what readers check. */
function guid(i: number): string {
  const chars = "0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz_$";
  let s = "";
  let x = i * 2654435761 + 97;
  for (let k = 0; k < 22; k++) { s += chars[Math.abs(x) % 64]; x = Math.floor(x / 64) + k * 7919 + i; }
  return s;
}

/** A space to write: its outline extruded to `height`, with properties and nested rooms. */
export interface IfcSpaceIn {
  name: string;
  kind: string;
  label: string;
  polygon: MultiPoly;
  height: number;
  props: [string, string | number][];
  rooms?: { name: string; polygon: MultiPoly }[];
}
export interface IfcStoreyIn { name: string; base: number; spaces: IfcSpaceIn[] }

/** Project → site → building → storeys → spaces, as IFC4. */
export function spacesIfc(name: string, buildingName: string, storeysIn: IfcStoreyIn[], origin: Pt = [0, 0]): string {
  const f = new Step();
  let gid = 0;
  const G = () => str(guid(++gid));
  const owner = f.add(`IFCOWNERHISTORY(${f.add(`IFCPERSONANDORGANIZATION(${f.add("IFCPERSON($,$,'Development Assistant',$,$,$,$,$)")},${f.add("IFCORGANIZATION($,'Bright Spaces',$,$,$)")},$)`)},${f.add(`IFCAPPLICATION(${f.add("IFCORGANIZATION($,'Bright Spaces',$,$,$)")},'1','Development Assistant','DA')`)},$,.ADDED.,$,$,$,0)`);
  const origin3 = f.add("IFCCARTESIANPOINT((0.,0.,0.))");
  const z = f.add("IFCDIRECTION((0.,0.,1.))"), x = f.add("IFCDIRECTION((1.,0.,0.))");
  const world = f.add(`IFCAXIS2PLACEMENT3D(${origin3},${z},${x})`);
  const ctx = f.add(`IFCGEOMETRICREPRESENTATIONCONTEXT($,'Model',3,1.E-05,${world},$)`);
  const body = f.add(`IFCGEOMETRICREPRESENTATIONSUBCONTEXT('Body','Model',*,*,*,*,${ctx},$,.MODEL_VIEW.,$)`);
  const units = f.add(`IFCUNITASSIGNMENT((${f.add("IFCSIUNIT(*,.LENGTHUNIT.,$,.METRE.)")},${f.add("IFCSIUNIT(*,.AREAUNIT.,$,.SQUARE_METRE.)")},${f.add("IFCSIUNIT(*,.VOLUMEUNIT.,$,.CUBIC_METRE.)")}))`);
  const project = f.add(`IFCPROJECT(${G()},${owner},${str(name)},$,$,$,$,(${ctx}),${units})`);

  const place = (rel: string | null, dx: number, dy: number, dz: number) =>
    f.add(`IFCLOCALPLACEMENT(${rel ?? "$"},${f.add(`IFCAXIS2PLACEMENT3D(${f.add(`IFCCARTESIANPOINT((${real(dx)},${real(dy)},${real(dz)}))`)},${z},${x})`)})`);
  const sitePl = place(null, origin[0], origin[1], 0);
  const site = f.add(`IFCSITE(${G()},${owner},'Teren',$,$,${sitePl},$,$,.ELEMENT.,$,$,$,$,$)`);
  const bldPl = place(sitePl, 0, 0, 0);
  const building = f.add(`IFCBUILDING(${G()},${owner},${str(buildingName)},$,$,${bldPl},$,$,.ELEMENT.,$,$,$)`);
  f.add(`IFCRELAGGREGATES(${G()},${owner},$,$,${project},(${site}))`);
  f.add(`IFCRELAGGREGATES(${G()},${owner},$,$,${site},(${building}))`);

  const solid = (mp: MultiPoly, depth: number): string[] => mp.map((poly) => {
    const ring = poly[0];
    const pts = [...ring, ring[0]].map(([px, py]) => f.add(`IFCCARTESIANPOINT((${real(px)},${real(py)}))`));
    const profile = f.add(`IFCARBITRARYCLOSEDPROFILEDEF(.AREA.,$,${f.add(`IFCPOLYLINE((${pts.join(",")}))`)})`);
    return f.add(`IFCEXTRUDEDAREASOLID(${profile},${f.add(`IFCAXIS2PLACEMENT3D(${origin3},$,$)`)},${z},${real(depth)})`);
  });
  const shape = (mp: MultiPoly, depth: number) => f.add(`IFCPRODUCTDEFINITIONSHAPE($,$,(${f.add(`IFCSHAPEREPRESENTATION(${body},'Body','SweptSolid',(${solid(mp, depth).join(",")}))`)}))`);
  const props = (of: string, entries: [string, string | number][]) => {
    const ps = entries.map(([k, v]) => f.add(`IFCPROPERTYSINGLEVALUE(${str(k)},$,${typeof v === "number" ? `IFCREAL(${real(v)})` : `IFCLABEL(${str(v)})`},$)`));
    const set = f.add(`IFCPROPERTYSET(${G()},${owner},'DA_Spatiu',$,(${ps.join(",")}))`);
    f.add(`IFCRELDEFINESBYPROPERTIES(${G()},${owner},$,$,(${of}),${set})`);
  };

  const storeys: string[] = [];
  for (const st of storeysIn) {
    const stPl = place(bldPl, 0, 0, st.base);
    const storey = f.add(`IFCBUILDINGSTOREY(${G()},${owner},${str(st.name)},$,$,${stPl},$,$,.ELEMENT.,${real(st.base)})`);
    storeys.push(storey);
    const spaces: string[] = [];
    for (const sp of st.spaces) {
      const space = f.add(`IFCSPACE(${G()},${owner},${str(sp.name)},${str(sp.kind)},$,${place(stPl, 0, 0, 0)},${shape(sp.polygon, sp.height)},${str(sp.label)},.ELEMENT.,.SPACE.,$)`);
      spaces.push(space);
      props(space, sp.props);
      // rooms nest inside their flat as spaces of their own
      const rooms = (sp.rooms ?? []).map((r) => f.add(`IFCSPACE(${G()},${owner},${str(r.name)},$,$,${place(stPl, 0, 0, 0)},${shape(r.polygon, sp.height)},${str(r.name)},.ELEMENT.,.INTERNAL.,$)`));
      if (rooms.length) f.add(`IFCRELAGGREGATES(${G()},${owner},$,$,${space},(${rooms.join(",")}))`);
    }
    if (spaces.length) f.add(`IFCRELAGGREGATES(${G()},${owner},$,$,${storey},(${spaces.join(",")}))`);
  }
  f.add(`IFCRELAGGREGATES(${G()},${owner},$,$,${building},(${storeys.join(",")}))`);

  const now = new Date().toISOString().slice(0, 19);
  return [
    "ISO-10303-21;", "HEADER;", "FILE_DESCRIPTION(('ViewDefinition [ReferenceView]'),'2;1');",
    `FILE_NAME(${str(name + ".ifc")},'${now}',('Development Assistant'),('Bright Spaces'),'','Development Assistant','');`,
    "FILE_SCHEMA(('IFC4'));", "ENDSEC;", "DATA;", ...f.lines, "ENDSEC;", "END-ISO-10303-21;", "",
  ].join("\n");
}

export function optionIfc(g: Generated, name: string, origin: Pt = [0, 0]): string {
  const KIND = { apartment: "Apartament", core: "Nucleu", corridor: "Coridor", retail: "Comercial", parking: "Parcare" } as const;
  return spacesIfc(name, `${g.option.typology} P+${g.option.storeys.length - 1}E`, g.option.storeys.map((s) => ({
    name: s.index === 0 ? "Parter" : `Etaj ${s.index}`,
    base: s.base,
    spaces: g.layout.floors[s.index].units.map((u) => ({
      name: u.id,
      kind: KIND[u.kind],
      label: u.kind === "apartment" ? `${u.id} ${u.type === "1" ? "Garsoniera" : `${u.type} camere`}` : `${u.id} ${KIND[u.kind]}`,
      polygon: u.polygon,
      height: s.cornice - s.base - 0.3,
      props: [["Tip", KIND[u.kind]], ["SuprafataConstruita", +u.area.toFixed(2)], ...(u.usable ? [["SuprafataUtila", +u.usable.toFixed(2)] as [string, number]] : []), ...(u.sunHours !== undefined ? [["OreSoare21Dec", +u.sunHours.toFixed(1)] as [string, number]] : [])],
      rooms: (u.rooms ?? []).map((r) => ({ name: r.kind, polygon: r.polygon })),
    })),
  })), origin);
}
